// A2F-650: client-side text → formatting-field detection for Step 2.
//
// Replaces the translate-rule dependency the original Step 2 wiring had
// — see PR #219 post-merge bug. translate-rule's `format` tool produces
// executor-shape rules (`apply["Heading 1"]["font.color"]`) that don't
// align with our Formatters-shape state (`tables.headerBg`, etc.). This
// module restores the A2F-546 keyword-detection approach (recovered
// from c5b732c^:lib/onboard-detect.ts) and routes its outputs into the
// FormattingFields namespace directly.
//
// A2F-705 reshapes the return type to two channels so the same prompt
// can land on dotted-key state (manualEdits) AND on the active table-
// style preset object in the same Apply. Multi-clause prompts split
// on "and" / "," / ";" / " with " so "blue table headers with white
// text" lands as one tableStylePatch with both headerBg and
// headerTextColor in the same Apply. Each clause runs the per-clause
// detector independently and the results are merged.
//
// A2F-706: heading element targets ("H1", "heading 1", "headings",
// etc.) now route both bold AND color into the FormattingFields state.
// The pre-A2F-706 comment said color was a "no-op pre-existing gap" —
// the gap was BASE_FORMATTING / CHRONA_DEFAULT lacking a headings.colors
// schema slot, which this PR fills. "navy H1" now lands as
// headings.colors.1 = "#1E3A8A"; bare "navy headings" (no number)
// routes to level 1 by convention — single ELEMENT_TARGETS entry,
// deterministic, the most common ask.

import type { FormattingFields } from '../data/onboard-v2-fields'
import type { TableStylePreset } from '../types'

// Lowercase keyword → hex color. Sorted at detection time so a multi-
// word entry ("navy blue") beats its prefix ("navy"). Recovered from
// the original A2F-546 COLOR_MAP.
const COLOR_MAP: Record<string, string> = {
  navy: '#1E3A8A',
  'navy blue': '#1E3A8A',
  blue: '#2563EB',
  'royal blue': '#1D4ED8',
  emerald: '#059669',
  green: '#059669',
  forest: '#166534',
  violet: '#7C3AED',
  purple: '#7C3AED',
  crimson: '#B91C1C',
  red: '#DC2626',
  teal: '#0D9488',
  cyan: '#0891B2',
  slate: '#334155',
  graphite: '#334155',
  charcoal: '#221610',
  burgundy: '#881337',
  maroon: '#881337',
  // Achromatic — added because "blue table headers with white text" is
  // a common ask and the chromatic-only map skipped it. Multi-word
  // entries before single ("light gray" beats "gray") thanks to the
  // length sort.
  white: '#FFFFFF',
  black: '#000000',
  gray: '#666666',
  grey: '#666666',
  'light gray': '#CCCCCC',
  'light grey': '#CCCCCC',
  'dark gray': '#333333',
  'dark grey': '#333333',
}

const COLOR_KEYS_BY_LENGTH = Object.keys(COLOR_MAP).sort(
  (a, b) => b.length - a.length,
)

function detectColor(text: string): string | null {
  const lower = text.toLowerCase()
  for (const key of COLOR_KEYS_BY_LENGTH) {
    if (lower.includes(key)) return COLOR_MAP[key]
  }
  return null
}

// Element-target detection. A target now describes both *where the
// color goes* (table preset palette field, or dotted FormattingFields
// path) AND *which heading level a bold flag goes on* when "bold" is
// present in the same clause. A2F-705 retargeted the table entries
// from `tables.*` (legacy single-preset slot, no longer rendered) to
// TableStylePreset palette fields applied against the active preset.
//
// Order matters — multi-word phrases must beat prefixes:
//   "header text" beats "header"
//   "even rows" beats "row"
//   "heading 1" beats "headings"
// Only color slots are valid `tableStyle` targets — keeps the patch
// type narrow so we don't need a `never` cast at the assign site.
type TableStyleColorField =
  | 'headerBg'
  | 'headerTextColor'
  | 'altRowBg'
  | 'defaultCellBg'
  | 'borderColor'

type Target =
  | { kind: 'tableStyle'; field: TableStyleColorField }
  | { kind: 'fields'; key: string }
  | { kind: 'headingLevel'; level: '1' | '2' | '3' }

const ELEMENT_TARGETS: ReadonlyArray<readonly [string, Target]> = [
  ['header text', { kind: 'tableStyle', field: 'headerTextColor' }],
  ['header color', { kind: 'tableStyle', field: 'headerBg' }],
  ['even rows', { kind: 'tableStyle', field: 'altRowBg' }],
  ['alternating rows', { kind: 'tableStyle', field: 'altRowBg' }],
  ['alternate rows', { kind: 'tableStyle', field: 'altRowBg' }],
  ['alt rows', { kind: 'tableStyle', field: 'altRowBg' }],
  ['odd rows', { kind: 'tableStyle', field: 'defaultCellBg' }],
  ['default rows', { kind: 'tableStyle', field: 'defaultCellBg' }],
  ['default cell', { kind: 'tableStyle', field: 'defaultCellBg' }],
  ['borders', { kind: 'tableStyle', field: 'borderColor' }],
  ['border', { kind: 'tableStyle', field: 'borderColor' }],
  ['caption', { kind: 'fields', key: 'figureCaption.color' }],
  ['heading 1', { kind: 'headingLevel', level: '1' }],
  ['heading 2', { kind: 'headingLevel', level: '2' }],
  ['heading 3', { kind: 'headingLevel', level: '3' }],
  ['h1', { kind: 'headingLevel', level: '1' }],
  ['h2', { kind: 'headingLevel', level: '2' }],
  ['h3', { kind: 'headingLevel', level: '3' }],
  // A2F-706: bare "heading" / "headings" (no level number) routes to
  // level 1 — most regulatory writers asking for "navy headings" mean
  // "navy section titles", which is H1. Single deterministic entry
  // beats trying to broadcast color across multiple levels (would
  // need a Target variant + multi-write reducer; not worth the surface
  // for the common ask). Placed after the numbered variants so
  // "heading 1" / "h1" still win their includes() match.
  ['heading', { kind: 'headingLevel', level: '1' }],
  ['headers', { kind: 'tableStyle', field: 'headerBg' }],
  ['header', { kind: 'tableStyle', field: 'headerBg' }],
  ['table', { kind: 'tableStyle', field: 'headerBg' }],
  // Standalone "text" — covers post-`with` clauses like "headers with
  // white text" (which splits into ["headers", "white text"], and the
  // second clause has no other element keyword). Positioned last so
  // more specific entries above always win: "header text" stays
  // headerTextColor, "blue text headers" still routes to headerBg via
  // the "headers" entry, "blue caption text" routes to figureCaption
  // via the earlier "caption" entry.
  ['text', { kind: 'tableStyle', field: 'headerTextColor' }],
] as const

const FALLBACK_COLOR_TARGET: Target = { kind: 'tableStyle', field: 'headerBg' }

function detectTarget(text: string): Target {
  const lower = text.toLowerCase()
  for (const [needle, target] of ELEMENT_TARGETS) {
    if (lower.includes(needle)) return target
  }
  return FALLBACK_COLOR_TARGET
}

// Two outcomes today: "times"/"serif" mention → Times New Roman; else
// no change. The default body font (Arial) covers everything else, so
// detecting "Arial" would be a no-op pill — better to skip and let the
// user see the "couldn't match" notice than to add a pill that
// visibly does nothing.
function detectSerifFont(text: string): string | null {
  const t = text.toLowerCase()
  if (t.includes('times') || t.includes('serif') || t.includes('tnr')) {
    return 'Times New Roman'
  }
  return null
}

function detectBold(text: string): boolean {
  // Whole-word match so we don't trip on "bolder" or substrings.
  return /\bbold\b/i.test(text)
}

export interface DetectFormatChangesResult {
  fieldChanges: FormattingFields
  tableStylePatch: Partial<TableStylePreset>
}

// Empty result helper — both channels empty means the caller
// surfaces the "couldn't match" notice instead of adding a no-op
// pill.
function isEmpty(r: DetectFormatChangesResult): boolean {
  return (
    Object.keys(r.fieldChanges).length === 0 &&
    Object.keys(r.tableStylePatch).length === 0
  )
}

// Run detection on a single clause. Bold + heading-level pairs in
// the clause flip the matching bool; color + table-target pairs
// land in tableStylePatch; color + caption fallback lands in
// fieldChanges. With only a color (no element keyword), fall back
// to the table header (most common ask).
function detectClause(clause: string): DetectFormatChangesResult {
  const out: DetectFormatChangesResult = {
    fieldChanges: {},
    tableStylePatch: {},
  }
  const trimmed = clause.trim()
  if (!trimmed) return out

  const target = detectTarget(trimmed)
  const color = detectColor(trimmed)
  const bold = detectBold(trimmed)
  const serif = detectSerifFont(trimmed)

  if (color) {
    if (target.kind === 'tableStyle') {
      out.tableStylePatch[target.field] = color
    } else if (target.kind === 'fields') {
      out.fieldChanges[target.key] = color
    } else if (target.kind === 'headingLevel') {
      // A2F-706: closes the pre-existing headingLevel + color no-op gap.
      // BASE_FORMATTING + CHRONA_DEFAULT.headings now have per-level
      // colors slots so "navy H1" lands on the right key and round-trips
      // through formattersFromState into formatters.headings.colors.1.
      out.fieldChanges[`headings.colors.${target.level}`] = color
    }
  }

  if (bold && target.kind === 'headingLevel') {
    out.fieldChanges[`headings.bold.${target.level}`] = true
  }

  if (serif) {
    out.fieldChanges['body.font'] = serif
    out.fieldChanges['headings.font'] = serif
    // Legacy `tables.bodyFont` parallel write keeps the preview's
    // table-font hint working for prompts saved before A2F-705 (and
    // for the time between this PR landing and any client cache
    // refresh). Active-preset patch is the source of truth that
    // saves and applies in Word — A2F-705 routes the table half of
    // a Times prompt to the active preset's bodyFont.
    out.fieldChanges['tables.bodyFont'] = serif
    out.tableStylePatch.bodyFont = serif
  }

  return out
}

/**
 * Map free text to FormattingFields deltas + TableStylePreset patch.
 * Returns null when nothing actionable was detected so the caller
 * can surface the unsupported notice instead of adding a no-op pill.
 *
 * Multi-clause prompts split on " and ", "," and ";" so each clause
 * runs the per-clause detector independently. Two-color, two-target
 * prompts like "emerald tables and bold navy H1" land as one
 * tableStylePatch entry (headerBg = emerald) plus one fieldChanges
 * entry (headings.bold.1 = true) — the navy color half is a no-op
 * because BASE_FORMATTING has no headings.color field.
 *
 * Color + element-keyword pairs target the matching slot; with no
 * element keyword the color falls back to the table headerBg slot
 * (the most common ask).
 *
 * Font detection currently only handles the serif/Times split. When
 * 'times' is detected in any clause, we apply to body.font +
 * headings.font + tables.bodyFont so the document sample shifts
 * cohesively rather than ending up with a Times body but Arial
 * headings. (tables.bodyFont is the legacy single-preset slot — the
 * configurator UI no longer surfaces it but the field stays for
 * backward-compat with profiles saved pre-A2F-701.)
 */
export function detectFormatChanges(
  text: string,
): DetectFormatChangesResult | null {
  const trimmed = text.trim()
  if (!trimmed) return null

  // Split on conjunctions / list separators. A leading or trailing
  // separator yields an empty chunk; detectClause filters those out.
  // " with " is included so "blue table headers with white text"
  // splits into ["blue table headers", "white text"], letting the
  // second clause find its own element target ("text" →
  // headerTextColor) and color ("white") independently.
  const chunks = trimmed.split(/(?:,|;|\sand\s|\swith\s)/i)
  const merged: DetectFormatChangesResult = {
    fieldChanges: {},
    tableStylePatch: {},
  }
  for (const chunk of chunks) {
    const clauseResult = detectClause(chunk)
    Object.assign(merged.fieldChanges, clauseResult.fieldChanges)
    Object.assign(merged.tableStylePatch, clauseResult.tableStylePatch)
  }

  if (isEmpty(merged)) return null
  return merged
}
